import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TrcaCtaGate from "@/components/TrcaCtaGate";
import PoweredByTendStrat from "@/components/PoweredByTendStrat";
import BillingToggle, { PriceLine } from "@/components/BillingToggle";
import { PRICING, ADVISOR_MAILTO } from "@/lib/plans";
import { PLAN_COPY } from "@/lib/planCopy";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, ShieldCheck, BarChart3, Calculator,
  FileText, Target, TrendingUp, AlertTriangle, Layers, Sparkles, Briefcase, Menu, X,
  Award, Compass, Scale, Building2, Factory, Zap, Droplet, Wrench, HardHat,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1749799798138-df205862e744?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxvaWwlMjByaWclMjBjb25zdHJ1Y3Rpb24lMjBmYWNpbGl0eSUyMGFlcmlhbHxlbnwwfHx8fDE3Nzc5NzUxOTd8MA&ixlib=rb-4.1.0&q=85";

// Decision → Framework (decision-led, not acronym-led).
const DECISIONS = [
  {
    decision: "Capability",
    code: "TRCA",
    name: "Tender Capability & Readiness Assessment",
    body: "Objectively evaluate whether your organisation is ready to pursue an opportunity before resources are committed.",
    icon: ShieldCheck,
    accent: "text-brand",
  },
  {
    decision: "Opportunity",
    code: "BNBD",
    name: "Bid / No Bid Decision",
    body: "Replace intuition with a weighted, defensible qualification of every incoming opportunity.",
    icon: Target,
    accent: "text-brand",
  },
  {
    decision: "Risk",
    code: "PCEI",
    name: "Project Commercial Exposure Index",
    body: "Quantify commercial and delivery risk exposure before pricing — not after the contract is signed.",
    icon: AlertTriangle,
    accent: "text-warn",
  },
  {
    decision: "Pricing",
    code: "PCAB",
    name: "Pricing Competency & Accuracy Benchmark",
    body: "Move from reactive quoting to evidence-based pricing benchmarked on your own tender history.",
    icon: BarChart3,
    accent: "text-brand",
  },
  {
    decision: "Performance",
    code: "TPAI",
    name: "Tender Performance Analytics & Intelligence",
    body: "Close the loop on every submission so each tender improves the next decision, not repeats the last mistake.",
    icon: TrendingUp,
    accent: "text-good",
  },
];

// "So what?" features — every card leads with a business benefit.
const CAPABILITIES = [
  { icon: Briefcase, title: "Single source of truth", body: "Know every live tender opportunity from one central location — no more scattered emails, drives and WhatsApp groups." },
  { icon: FileText,  title: "Structured documentation", body: "Keep every RFQ, BOQ, drawing and correspondence organised, versioned and instantly accessible." },
  { icon: Target,    title: "Prioritise the right opportunities", body: "Score and rank incoming tenders before your team spends a rupee of bid effort chasing the wrong work." },
  { icon: Calculator,title: "Pricing confidence", body: "Improve pricing discipline using structured evaluation and historical benchmarks — not assumptions." },
  { icon: Compass,   title: "Commercial risk visibility", body: "Surface commercial exposure and delivery risk before bid submission, not during contract execution." },
  { icon: TrendingUp,title: "Continuous improvement", body: "Every win, loss and no-bid becomes evidence that sharpens the next tender decision." },
];

// Reframed 120-day journey — organisational transformation, not software onboarding.
const JOURNEY = [
  { d: "Day 0–30",   t: "Baseline & Audit",       b: "Establish a capability baseline with TRCA. Identify governance gaps across capability, risk, pricing and performance." },
  { d: "Day 31–60",  t: "Qualify Strategically",  b: "Embed BNBD across every incoming opportunity. Introduce structured qualification and stop pursuing the wrong tenders." },
  { d: "Day 61–90",  t: "Price with Discipline",  b: "Activate PCEI risk profiling and PCAB pricing benchmarks. Bid prices become defensible and consistent." },
  { d: "Day 91–120", t: "Institutionalise Learning", b: "Close the loop with TPAI. Every tender outcome feeds back into calibrated frameworks and executive intelligence." },
];

// Traditional bidding vs TWIN Governance
const COMPARISON = [
  { left: "Bid everything",            right: "Qualify strategically" },
  { left: "Gut feeling",                right: "Structured decision framework" },
  { left: "Scattered spreadsheets",     right: "Central governance platform" },
  { left: "Reactive pricing",           right: "Evidence-based pricing" },
  { left: "No organisational learning", right: "Continuous performance intelligence" },
  { left: "Risk discovered post-bid",   right: "Risk quantified before submission" },
];

// Who is TWIN for — vertical fit.
const AUDIENCES = [
  { icon: HardHat,  label: "EPC Contractors" },
  { icon: Factory,  label: "Industrial Manufacturers" },
  { icon: Zap,      label: "Oil & Gas Suppliers" },
  { icon: Wrench,   label: "Engineering Consultants" },
  { icon: Building2,label: "Infrastructure Companies" },
  { icon: Droplet,  label: "Power & Utility Organisations" },
];

// Founder credibility markers.
const FOUNDER_MARKERS = [
  "30+ Years of Strategic Tendering Experience",
  "EPC, Oil & Gas, Power, Water & Infrastructure",
  "Proven Tender Governance Methodology",
  "Practical Decision Frameworks",
];

// Pricing — 3 self-serve tiers + Enterprise.
const PLANS = [
  ...PLAN_COPY.map((p) => ({ ...p, accent: p.key === "explorer" ? "starter" : p.key, buy: p.key })),
  {
    plan: "TWIN Enterprise",
    eyebrow: "Contact for Pricing",
    headline: "Enterprise-wide tender governance.",
    desc: "Everything in Professional, plus custom features and multiple team users as needed.",
    feats: [
      "Everything in TWIN Professional",
      "Custom scoring models",
      "Multi-business-unit support",
      "ERP & API integration",
      "Executive reporting suite",
      "Dedicated success manager",
    ],
    cta: "Contact Sales",
    accent: "enterprise",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const ctaTo = user ? "/dashboard" : "/register";
  const [cycle, setCycle] = useState("monthly");
  const signInTo = user ? "/dashboard" : "/login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Sticky Navigation */}
      <header
        className={`sticky top-0 z-40 transition-all duration-150 ${
          scrolled ? "bg-white/95 backdrop-blur-md border-b border-ink-line" : "bg-transparent"
        }`}
        data-testid="landing-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className={`w-8 h-8 grid place-items-center font-display font-bold tracking-tight rounded-sm ${scrolled ? "bg-brand text-white" : "bg-white text-brand"}`}>T</div>
            <div className="leading-none">
              <div className={`font-display font-bold text-[15px] tracking-tight ${scrolled ? "text-ink" : "text-white"}`}>TWIN</div>
              <div className={`text-[9px] uppercase tracking-[0.2em] mt-1 ${scrolled ? "text-ink-muted" : "text-white/70"}`}>Tender Governance</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-[13px]">
            <a href="#why" className={`transition-colors ${scrolled ? "text-ink-muted hover:text-ink" : "text-white/80 hover:text-white"}`} data-testid="nav-why">Why TWIN</a>
            <a href="#framework" className={`transition-colors ${scrolled ? "text-ink-muted hover:text-ink" : "text-white/80 hover:text-white"}`} data-testid="nav-framework">Framework</a>
            <a href="#journey" className={`transition-colors ${scrolled ? "text-ink-muted hover:text-ink" : "text-white/80 hover:text-white"}`} data-testid="nav-journey">120-Day Journey</a>
            <a href="#audience" className={`transition-colors ${scrolled ? "text-ink-muted hover:text-ink" : "text-white/80 hover:text-white"}`} data-testid="nav-audience">Who It&apos;s For</a>
            <a href="#pricing" className={`transition-colors ${scrolled ? "text-ink-muted hover:text-ink" : "text-white/80 hover:text-white"}`} data-testid="nav-pricing-section">Pricing</a>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="nav-go-dashboard"
                className="bg-warn hover:bg-warn/90 text-white px-4 py-2 text-[13px] font-medium rounded-sm flex items-center gap-1.5 transition-colors"
              >
                Open Dashboard <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : (
              <>
                <Link to="/login" className={`text-[13px] transition-colors ${scrolled ? "text-ink hover:text-brand" : "text-white hover:text-warn"}`} data-testid="nav-login">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register"
                  className={`px-4 py-2 text-[13px] font-medium rounded-sm flex items-center gap-1.5 transition-colors ${scrolled ? "bg-brand hover:bg-brand-hover text-white" : "bg-warn hover:bg-warn/90 text-white"}`}
                >
                  Start free <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setMobileOpen((s) => !s)}
            className={`lg:hidden p-2 ${scrolled ? "text-ink" : "text-white"}`}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-ink-line bg-white" data-testid="mobile-menu">
            <div className="px-6 py-4 flex flex-col gap-4 text-sm">
              <a href="#why" onClick={() => setMobileOpen(false)} className="text-ink-muted">Why TWIN</a>
              <a href="#framework" onClick={() => setMobileOpen(false)} className="text-ink-muted">Framework</a>
              <a href="#journey" onClick={() => setMobileOpen(false)} className="text-ink-muted">120-Day Journey</a>
              <a href="#audience" onClick={() => setMobileOpen(false)} className="text-ink-muted">Who It&apos;s For</a>
              <a href="#pricing" onClick={() => setMobileOpen(false)} className="text-ink-muted">Pricing</a>
              <Link to={signInTo} onClick={() => setMobileOpen(false)} className="bg-brand text-white px-4 py-2 text-sm rounded-sm flex items-center justify-center gap-1.5">
                {user ? "Open Dashboard" : "Sign in"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section
        className="relative -mt-16 pt-16 min-h-[88vh] flex items-end overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,15,50,0.55) 0%, rgba(0,15,50,0.85) 70%, rgba(0,15,50,0.95) 100%), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 w-full">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 chip border-white/30 bg-white/10 text-white mb-8" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 bg-warn rounded-full animate-pulse" />
              <span className="font-num">Tender Governance Platform · Built for EPC &amp; Industrial</span>
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[0.92] font-bold tracking-tight text-white">
              Tendering isn&apos;t luck.<br />
              <span className="text-warn">It&apos;s governance.</span>
            </h1>

            {/* Corporate ownership marker — subtle, below headline. */}
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.25em] text-white/60 font-num">
                Tender Win Intelligence Navigator&trade;
              </span>
              <span className="hidden sm:inline h-3 w-px bg-white/20" />
              <PoweredByTendStrat variant="dark" size="md" />
            </div>

            <p className="text-white/75 text-lg lg:text-xl max-w-2xl mt-8 leading-relaxed">
              TWIN transforms tendering from disconnected activities into a structured governance system —
              built on five proprietary decision frameworks and three decades of strategic tendering experience
              in EPC, Oil &amp; Gas, Power, Water and Infrastructure.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-10">
              <Link
                to={ctaTo}
                data-testid="hero-cta-primary"
                className="bg-warn hover:bg-warn/90 text-white px-6 py-3.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors group"
              >
                Start Free with TWIN Explorer
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a
                href="#pricing"
                data-testid="hero-cta-secondary"
                className="border border-white/30 hover:border-white text-white px-6 py-3.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors"
              >
                Book a Live Demonstration
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mt-20 pt-10 border-t border-white/15 max-w-3xl">
              {[
                { v: "5",   l: "Governance frameworks" },
                { v: "30+", l: "Years of EPC experience" },
                { v: "120d",l: "To disciplined tender governance" },
                { v: "1×",  l: "Single source of truth" },
              ].map((s) => (
                <div key={s.l} data-testid={`stat-${s.l.replace(/\s/g, "-").toLowerCase()}`}>
                  <div className="font-num text-3xl lg:text-4xl font-bold text-white">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-wider text-white/60 mt-1.5">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY TWIN EXISTS */}
      <section id="why" className="border-y border-ink-line bg-white" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Why TWIN Exists</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
              Most organisations know how to prepare a bid.<br />
              <span className="text-ink-muted">Few have a system for deciding which to pursue.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[15px] text-ink leading-relaxed">
            <p>
              Tendering is not about submitting more bids. It is about making better decisions.
              Yet in most EPC and industrial organisations, tender decisions still rely on intuition,
              scattered documentation and post-mortem analysis of losses.
            </p>
            <p className="text-ink-muted">
              TWIN provides the missing governance layer — a structured system for deciding which
              opportunities to pursue, how to evaluate capability, how to assess commercial risk,
              how to price strategically, and how to learn from every tender outcome.
            </p>
            <ul className="pt-4 space-y-3 border-t border-ink-line">
              {[
                "Which opportunities to pursue",
                "How to evaluate capability objectively",
                "How to assess commercial risk before submission",
                "How to price with evidence, not assumption",
                "How to learn from previous tenders",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-[15px]">
                  <CheckCircle2 className="w-4 h-4 text-brand mt-1 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24 text-white bg-[#0A1633]" data-testid="founder-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-warn">Founder</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
              Built from three decades<br />
              <span className="text-warn">of strategic tendering.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-start gap-5">
              <div className="w-16 h-16 grid place-items-center rounded-sm bg-warn/15 text-warn shrink-0">
                <Award className="w-7 h-7" />
              </div>
              <div>
                <div className="font-display font-bold text-2xl tracking-tight">Srijit Vasudevan</div>
                <div className="text-[12px] uppercase tracking-wider text-white/60 mt-1">Founder &amp; Principal Advisor</div>
              </div>
            </div>
            <p className="text-white/75 text-[15px] leading-relaxed mt-6 max-w-2xl">
              TWIN is not a generic tender application dressed as software. It is codified experience —
              built from more than 30 years of helping EPC and industrial organisations improve their
              tender capability, price with discipline and reduce commercial exposure.
            </p>
            <p className="text-white/60 text-[15px] leading-relaxed mt-4 max-w-2xl">
              The five frameworks inside TWIN are the same frameworks used to advise leadership teams on
              real tenders across Oil &amp; Gas, Power, Water, Infrastructure and Industrial manufacturing.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {FOUNDER_MARKERS.map((m) => (
                <div key={m} className="flex items-start gap-2.5 text-[13px] text-white/85">
                  <CheckCircle2 className="w-4 h-4 text-warn mt-0.5 shrink-0" />
                  <span>{m}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="border-y border-ink-line bg-white" data-testid="problem-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">The Problem</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
              Bidding is broken.<br />
              <span className="text-ink-muted">Decisions remain anecdotal.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8 mt-12 max-w-5xl">
            {[
              "Tender opportunities arrive through emails, portals, WhatsApp groups and sales teams with no single source of truth.",
              "Teams pursue opportunities based on intuition instead of structured qualification.",
              "Commercial risks are frequently discovered after the bid is submitted — sometimes after the contract is signed.",
              "Lessons from previous tenders are rarely captured. The same mistakes are repeated the following quarter.",
            ].map((p, i) => (
              <div key={i} className="border-l-2 border-warn pl-5" data-testid={`problem-item-${i + 1}`}>
                <div className="font-num text-[11px] text-warn uppercase tracking-wider">0{i + 1}</div>
                <p className="text-[15px] text-ink mt-2 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FRAMEWORK — Decision-led */}
      <section id="framework" className="py-24" data-testid="framework-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-14">
            <div className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">The Framework</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
                Five decisions every<br />
                <span className="text-brand">winning tender requires.</span>
              </h2>
            </div>
            <p className="text-ink-muted text-base leading-relaxed lg:pt-12">
              Each framework is a piece of governance discipline — not a dashboard ornament.
              Every decision compounds into a defensible, repeatable tender system.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-ink-line">
            {DECISIONS.map((m, i) => (
              <div
                key={m.code}
                className={`p-7 bg-white hover:bg-secondary/50 transition-colors duration-150 border-ink-line ${
                  i % 3 !== 2 ? "lg:border-r" : ""
                } ${i % 2 !== 1 ? "md:border-r lg:border-r" : ""} ${i < DECISIONS.length - 1 ? "border-b" : ""} ${
                  i >= 3 ? "lg:border-b-0" : ""
                }`}
                data-testid={`framework-card-${m.code}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-10 h-10 grid place-items-center bg-secondary rounded-sm ${m.accent}`}>
                    <m.icon className="w-5 h-5" />
                  </div>
                  <div className="font-num text-[11px] text-ink-muted uppercase tracking-wider">0{i + 1}</div>
                </div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">Decision · {m.decision}</div>
                <div className="font-display font-bold text-2xl tracking-tight mt-1.5">{m.code}</div>
                <div className="text-[12px] uppercase tracking-wider text-ink-muted mt-1">{m.name}</div>
                <p className="text-sm text-ink mt-4 leading-relaxed">{m.body}</p>
              </div>
            ))}
            {/* CTA cell */}
            <div className="p-7 bg-brand text-white border-ink-line border-b lg:border-b-0" data-testid="framework-cta-card">
              <Sparkles className="w-5 h-5 text-warn mb-6" />
              <div className="font-display font-bold text-2xl tracking-tight">One integrated governance system.</div>
              <p className="text-white/80 text-sm mt-4 leading-relaxed">
                Framework scores compound into a single organisational governance signal. No silos.
                No parallel dashboards. One repeatable system.
              </p>
              <Link to={ctaTo} className="inline-flex items-center gap-2 text-warn font-medium mt-6 text-sm group" data-testid="framework-card-cta">
                Start with TWIN Explorer <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES / FEATURES */}
      <section id="features" className="py-24 bg-secondary/40 border-y border-ink-line" data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Capabilities</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">What TWIN gives you.</h2>
            </div>
            <p className="text-ink-muted max-w-md text-sm leading-relaxed">
              Every capability answers a single question: what does this change about the way our organisation
              makes tender decisions?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CAPABILITIES.map((f) => (
              <div key={f.title} className="card-flat p-6 group" data-testid={`feature-${f.title.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-9 h-9 grid place-items-center bg-brand-soft text-brand rounded-sm mb-5">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="font-display font-bold text-lg tracking-tight">{f.title}</div>
                <p className="text-sm text-ink-muted mt-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-24 bg-white" data-testid="comparison-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">The Difference</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
              Traditional tendering<br />
              <span className="text-brand">vs. TWIN governance.</span>
            </h2>
          </div>

          <div className="border border-ink-line">
            <div className="grid grid-cols-2 bg-secondary/60 border-b border-ink-line">
              <div className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] text-ink-muted border-r border-ink-line">Traditional Tendering</div>
              <div className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] text-brand">TWIN Governance</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className={`grid grid-cols-2 ${i < COMPARISON.length - 1 ? "border-b border-ink-line" : ""}`} data-testid={`comparison-row-${i + 1}`}>
                <div className="px-6 py-5 border-r border-ink-line flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-warn shrink-0" />
                  <span className="text-[15px] text-ink-muted line-through decoration-warn/40">{row.left}</span>
                </div>
                <div className="px-6 py-5 flex items-center gap-3 bg-brand-soft/40">
                  <CheckCircle2 className="w-4 h-4 text-brand shrink-0" />
                  <span className="text-[15px] text-ink font-medium">{row.right}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS TWIN FOR */}
      <section id="audience" className="py-24 bg-secondary/40 border-y border-ink-line" data-testid="audience-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mb-12">
            <div className="lg:col-span-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Designed For</div>
              <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
                Enterprise tender teams<br />
                <span className="text-brand">that bid strategically.</span>
              </h2>
            </div>
            <p className="text-ink-muted text-base leading-relaxed lg:pt-12">
              TWIN is built for CEOs, Managing Directors, Commercial Directors, Tender &amp; Proposal
              Managers and Business Development Heads.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIENCES.map((a) => (
              <div key={a.label} className="card-flat p-6 flex items-center gap-4" data-testid={`audience-${a.label.replace(/\s/g, "-").toLowerCase()}`}>
                <div className="w-11 h-11 grid place-items-center bg-brand-soft text-brand rounded-sm shrink-0">
                  <a.icon className="w-5 h-5" />
                </div>
                <div className="font-display font-medium text-[15px] tracking-tight">{a.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 120-DAY JOURNEY */}
      <section id="journey" className="py-24" data-testid="journey-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-16">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Implementation Roadmap</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
              From reactive bidding to disciplined tender governance.
            </h2>
            <p className="text-ink-muted text-base mt-5 leading-relaxed">
              A 120-day organisational transformation programme that helps tender teams transition from
              disconnected activities to a repeatable governance system — with measurable signals every 30 days.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-0 right-0 top-7 h-px bg-ink-line hidden md:block" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {JOURNEY.map((j, i) => (
                <div key={j.d} className="relative" data-testid={`journey-step-${i + 1}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-14 h-14 bg-brand text-white grid place-items-center font-num font-bold text-sm rounded-sm border border-brand">
                      0{i + 1}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-warn font-num">{j.d}</div>
                  </div>
                  <div className="font-display font-bold text-xl tracking-tight">{j.t}</div>
                  <p className="text-sm text-ink-muted mt-2 leading-relaxed">{j.b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING — 4 tiers, pricing-neutral */}
      <section id="pricing" className="py-24 bg-secondary/40 border-y border-ink-line" data-testid="pricing-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Pricing</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Choose your level of tender governance.</h2>
            <p className="text-ink-muted text-base mt-4">
              Start with a 14-day free trial of Explorer — no credit card required. Move to Growth for
              structured Bid/No-Bid decisions, and to Professional for complete tender governance.
            </p>
          </div>

          <div className="mb-8"><BillingToggle cycle={cycle} onChange={setCycle} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {PLANS.map((p) => (
              <div
                key={p.plan}
                className={`p-7 transition-all duration-150 rounded-sm flex flex-col ${
                  p.featured
                    ? "bg-brand text-white border border-brand xl:-translate-y-2 shadow-lg"
                    : "bg-white border border-ink-line hover:-translate-y-1 hover:shadow-sm"
                }`}
                data-testid={`pricing-card-${p.accent}`}
              >
                {p.featured && (
                  <div className="inline-block bg-warn text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium mb-4 self-start">
                    Recommended
                  </div>
                )}
                <div className={`text-[10px] uppercase tracking-[0.18em] font-medium ${p.featured ? "text-warn" : "text-brand"}`} data-testid={`pricing-tag-${p.accent}`}>{p.eyebrow}</div>
                <div className={`font-display text-xl font-bold tracking-tight mt-2 ${p.featured ? "text-white" : "text-ink"}`}>{p.plan}</div>
                {p.buy ? <PriceLine plan={p.buy} cycle={cycle} pricing={PRICING} featured={p.featured} /> : <div className="font-num text-2xl font-bold mt-4">Custom</div>}
                <div className={`font-display font-semibold mt-5 leading-snug ${p.featured ? "text-white" : "text-ink"}`}>{p.headline}</div>
                <p className={`text-sm mt-2 leading-relaxed ${p.featured ? "text-white/80" : "text-ink-muted"}`}>{p.desc}</p>

                <ul className="mt-6 space-y-3 border-t pt-6 text-sm flex-1" style={{ borderColor: p.featured ? "rgba(255,255,255,0.15)" : "#E5E7EB" }}>
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${p.featured ? "text-warn" : "text-good"}`} />
                      <span className={p.featured ? "text-white/90" : "text-ink"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={p.key === "explorer" && !user ? "/register" : p.buy ? `/plans?plan=${p.buy}&cycle=${cycle}` : ADVISOR_MAILTO}
                  onClick={(e) => { if (p.accent === "enterprise") { e.preventDefault(); window.location.href = ADVISOR_MAILTO; } }}
                  data-testid={`pricing-cta-${p.accent}`}
                  className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-sm transition-colors ${
                    p.featured
                      ? "bg-white text-brand hover:bg-warn hover:text-white"
                      : "bg-brand text-white hover:bg-brand-hover"
                  }`}
                >
                  {p.cta} <ArrowRight className="w-4 h-4" />
                </Link>
                {p.closing && <p className={`text-[12px] italic mt-4 leading-relaxed ${p.featured ? "text-white/75" : "text-ink-muted"}`}>{p.closing}</p>}
                {p.footnote && <p className={`text-[11px] mt-2 ${p.featured ? "text-white/60" : "text-ink-muted"}`}>{p.footnote}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 120-DAY JOURNEY — Premium mentoring block */}
      <section className="py-24 text-white bg-[#0A1633]" data-testid="mentoring-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="border border-warn/40 rounded-sm p-10 lg:p-14 bg-gradient-to-br from-transparent to-warn/5">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-warn">Advisory Programme</div>
                <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
                  Accelerate your results with the<br />
                  <span className="text-warn">TWIN 120-Day Journey.</span>
                </h2>
                <p className="text-white/75 text-[15px] leading-relaxed mt-6 max-w-lg">
                  The platform delivers the technology. The 120-Day Journey helps your organisation
                  successfully implement governance, train teams, calibrate frameworks and embed
                  disciplined tender decision-making.
                </p>
                <p className="text-white/60 text-[13px] leading-relaxed mt-4 max-w-lg">
                  Delivered by the founder and senior tender governance advisors — not by a customer
                  success desk reading from a playbook.
                </p>
                <Link
                  to={ctaTo}
                  data-testid="mentoring-cta"
                  className="inline-flex items-center gap-2 mt-8 bg-warn hover:bg-warn/90 text-white px-6 py-3.5 text-sm font-medium rounded-sm group transition-colors"
                >
                  Book a Discovery Session
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>

              <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { i: Scale,      t: "Weekly mentoring",        b: "Structured sessions with the founder and senior advisors." },
                  { i: ShieldCheck,t: "Governance implementation",b: "Framework rollout across your commercial team." },
                  { i: BarChart3,  t: "Framework calibration",    b: "Thresholds and scoring tuned to your organisation." },
                  { i: Layers,     t: "Team enablement",          b: "Training and onboarding for tender and proposal managers." },
                  { i: Target,     t: "Live tender reviews",      b: "Real opportunities reviewed alongside your team." },
                  { i: TrendingUp, t: "Executive reporting",      b: "Governance dashboards for leadership visibility." },
                ].map((x) => (
                  <div key={x.t} className="border border-white/10 bg-white/[0.03] rounded-sm p-5" data-testid={`mentoring-item-${x.t.replace(/\s/g, "-").toLowerCase()}`}>
                    <div className="w-9 h-9 grid place-items-center rounded-sm bg-warn/15 text-warn mb-4">
                      <x.i className="w-4 h-4" />
                    </div>
                    <div className="font-display font-bold text-[15px] tracking-tight text-white">{x.t}</div>
                    <p className="text-[13px] text-white/60 mt-1.5 leading-relaxed">{x.b}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRCA QUICK CTA (retained from existing gate to avoid disturbing the flow) */}
      <section className="py-16 bg-white border-y border-ink-line" data-testid="trca-cta-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-6">
          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Start With Capability</div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-2 leading-tight">
              Assess your tender readiness in 15 minutes.
            </h3>
            <div className="mt-6 border border-ink-line rounded-sm p-6 bg-secondary/40" data-testid="trca-readiness-card">
              <div className="text-[11px] uppercase tracking-[0.2em] text-brand font-medium">Organisational Readiness Assessment</div>
              <div className="font-display text-xl font-bold tracking-tight mt-2">Before You Compete, Know Your Capabilities.</div>
              <p className="text-ink-muted text-[14px] mt-1.5 leading-relaxed">
                Discover how ready your organisation is to compete effectively in tenders.
              </p>
              <div className="flex items-baseline gap-2 mt-4" data-testid="trca-readiness-price">
                <span className="font-num text-3xl font-bold tracking-tight">$149</span>
                <span className="text-sm text-ink-muted">One-time assessment</span>
              </div>
              <ul className="mt-4 space-y-2 text-[14px]">
                {[
                  "20-prompt organisational assessment",
                  "Six tendering competency domains",
                  "100-point capability score",
                  "Identify strengths and critical gaps",
                  "Understand improvement priorities",
                ].map((f) => (
                  <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-good shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
            <p className="text-ink-muted text-[14px] mt-4 leading-relaxed">
              Complete the TRCA Assessment to receive a capability baseline before you configure your governance system.
            </p>
          </div>
          <TrcaCtaGate>
            {({ start, loading }) => (
              <button
                onClick={start}
                disabled={loading}
                data-testid="trca-strip-cta"
                className="bg-brand hover:bg-brand-hover disabled:opacity-70 text-white px-6 py-3.5 text-sm font-medium rounded-sm flex items-center gap-2 transition-colors group"
              >
                {loading ? "Checking…" : "Take the TRCA Assessment"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </TrcaCtaGate>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-secondary/40" data-testid="final-cta-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-brand">Get Started</div>
              <h2 className="font-display text-4xl lg:text-6xl font-bold tracking-tight mt-3 leading-[0.95]">
                From bidding to<br />
                <span className="text-brand">tender governance.</span>
              </h2>
              <p className="text-ink-muted text-lg mt-6 max-w-lg leading-relaxed">
                Start with TWIN Explorer today. Introduce structured governance to your next tender cycle
                and scale as your capability matures.
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <Link
                to={ctaTo}
                data-testid="final-cta-primary"
                className="bg-brand hover:bg-brand-hover text-white px-6 py-4 text-sm font-medium rounded-sm flex items-center justify-between gap-3 group"
              >
                <span>{user ? "Open your dashboard" : "Start Free with TWIN Explorer"}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              {!user && (
                <a
                  href="#pricing"
                  data-testid="final-cta-demo"
                  className="border border-ink-line hover:border-brand text-ink px-6 py-4 text-sm font-medium rounded-sm flex items-center justify-between gap-3"
                >
                  <span>Book a Live Demonstration</span>
                  <ArrowUpRight className="w-5 h-5" />
                </a>
              )}
              <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-2 flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-good" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-good" /> Cancel anytime</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-good" /> Enterprise-grade governance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 bg-white border-t border-ink-line" data-testid="footer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-brand grid place-items-center text-white font-display font-bold tracking-tight rounded-sm">T</div>
              <div>
                <div className="font-display font-bold text-[15px] tracking-tight">TWIN</div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-ink-muted mt-1">Tender Win Intelligence Navigator</div>
              </div>
            </div>
            <p className="text-sm text-ink-muted mt-5 max-w-sm leading-relaxed">
              A structured tender governance platform for EPC and industrial organisations.
              Built on decades of strategic tendering experience — not on generic bid management software.
            </p>

            {/* Ownership marker */}
            <div className="mt-6 pt-6 border-t border-ink-line">
              <PoweredByTendStrat variant="light" size="md" />
              <p className="text-[12px] text-ink-muted mt-2.5">
                TWIN&trade; is a product of <span className="text-ink font-medium">TendStrat Solutions</span>.
              </p>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-3">Platform</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#why" className="hover:text-brand">Why TWIN</a></li>
              <li><a href="#framework" className="hover:text-brand">Framework</a></li>
              <li><a href="#journey" className="hover:text-brand">120-Day Journey</a></li>
              <li><a href="#pricing" className="hover:text-brand">Pricing</a></li>
              <li><Link to={signInTo} className="hover:text-brand">{user ? "Dashboard" : "Sign in"}</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#" className="hover:text-brand">About</a></li>
              <li><a href="#" className="hover:text-brand">Contact</a></li>
              <li><a href="#" className="hover:text-brand">Privacy</a></li>
              <li><a href="#" className="hover:text-brand">Terms</a></li>
            </ul>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-ink-line flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-wider text-ink-muted">
          <div>© {new Date().getFullYear()} TendStrat Solutions. All rights reserved.</div>
          <div className="font-num">Tender Governance · Built for EPC &amp; Industrial</div>
        </div>
      </footer>
    </div>
  );
}
